import { useState, useEffect } from 'react';
import { FlowNode } from '../../types';
import { AutoResizeTextarea, JsonTextarea } from './JsonTextarea';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

export interface KvRow { id: string; key: string; value: string; }

const HEADER_FORMATS = ['seconds', 'epoch-s', 'epoch-ms', 'http-date'] as const;
type HeaderFormat = (typeof HEADER_FORMATS)[number];

// Extra codes a user can opt into. 429 is always retried and is not listed here.
const OPT_IN_STATUSES = [500, 502, 503, 504, 408, 425];

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_MAX_DELAY = 60;

interface RateLimitRow { id: string; name: string; format: string; }

// Free text such as "404, 410" → [404, 410]. Anything that is not a number is dropped.
export function parseStatusList(text: string): number[] {
  const codes = text.split(/[\s,;]+/).filter(Boolean).map(Number).filter(Number.isInteger);
  return [...new Set(codes)].sort((a, b) => a - b);
}

// The worker lower-cases and trims before matching, so "Epoch-S" and " epoch-s " are both valid.
// An unrecognised value is kept verbatim rather than aliased to 'seconds': aliasing would show a
// valid format the runtime never honours, and picking that same value in the dropdown would be a
// no-op, leaving the user no way to repair the row.
function normalizeFormat(raw: unknown): string {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return (HEADER_FORMATS as readonly string[]).includes(v) ? v : (typeof raw === 'string' ? raw : 'seconds');
}

function isKnownFormat(v: string): boolean {
  return (HEADER_FORMATS as readonly string[]).includes(v.trim().toLowerCase());
}

interface RetryCfg {
  enabled?: boolean;
  maxAttempts?: number;
  maxDelaySeconds?: number;
  retryOn?: number[];
  retryOnNetworkErrors?: boolean;
  rateLimitHeaders?: Array<{ name?: string; format?: string }>;
}

function newRow(): KvRow {
  return { id: crypto.randomUUID(), key: '', value: '' };
}

export function objToRows(obj: unknown): KvRow[] {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
  return Object.entries(obj as Record<string, unknown>).map(([k, v]) => ({
    id: crypto.randomUUID(),
    key: k,
    value: String(v ?? ''),
  }));
}

export function rowsToObj(rows: KvRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of rows) {
    if (r.key.trim()) out[r.key.trim()] = r.value;
  }
  return out;
}

// Replace {{...}} placeholders with valid JSON stand-ins so the structure can be
// validated with JSON.parse even when the template contains unquoted expressions.
function sanitizeTemplateForValidation(template: string): string {
  return template
    // "{{expr}}" or "{{expr}} ?? fallback" (entire quoted string value) → keep quotes
    .replace(/"(\{\{[^}]+\}\}[^"]*)"/g, '"__placeholder__"')
    // bare {{expr}} (unquoted — meant to be a number/bool/null) → null
    .replace(/\{\{[^}]+\}\}/g, 'null');
}

/// Canonical view of a retry block: defaults filled in, retryOn sorted. Two blocks with the same
/// canonical form behave identically at runtime, whatever they look like on disk.
function effectiveRetry(retry: RetryCfg | undefined): Record<string, unknown> {
  const r = (retry ?? {}) as RetryCfg & Record<string, unknown>;
  const {
    enabled, maxAttempts, maxDelaySeconds, retryOn, retryOnNetworkErrors, rateLimitHeaders,
    ...rest
  } = r;
  return {
    ...rest,
    enabled: enabled !== false,
    maxAttempts: maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
    maxDelaySeconds: maxDelaySeconds ?? DEFAULT_MAX_DELAY,
    retryOn: [...new Set(retryOn ?? [])].sort((a, b) => a - b),
    retryOnNetworkErrors: retryOnNetworkErrors === true,
    rateLimitHeaders: (rateLimitHeaders ?? [])
      .filter(h => h && (h.name ?? '').trim())
      .map(h => ({ name: (h.name ?? '').trim(), format: normalizeFormat(h.format) })),
  };
}

function parseConfig(config: Record<string, unknown> | undefined) {
  const bodyObj = config?.body;
  const retry = (config?.retry ?? undefined) as RetryCfg | undefined;

  // Anything the panel has no field for — initialDelaySeconds today, whatever is added to the
  // schema tomorrow — is carried through untouched. The write-back rebuilds `retry` from scratch,
  // so without this an edit to an unrelated field would silently delete it.
  const {
    enabled: _e, maxAttempts: _ma, maxDelaySeconds: _md,
    retryOn: _ro, retryOnNetworkErrors: _rne, rateLimitHeaders: _rlh,
    ...retryPassthrough
  } = (retry ?? {}) as RetryCfg & Record<string, unknown>;

  const storedRetryOn = retry?.retryOn ?? [];
  const retryFields = {
    retryEnabled: retry?.enabled !== false,
    maxAttempts: retry?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
    maxDelaySeconds: retry?.maxDelaySeconds ?? DEFAULT_MAX_DELAY,
    timeoutSeconds: (config?.timeoutSeconds as number | undefined) ?? null,
    allowStatusText: ((config?.allowStatus as number[] | undefined) ?? []).join(', '),
    retryOn: [...new Set(storedRetryOn.filter(c => OPT_IN_STATUSES.includes(c)))],
    // Codes outside the checkbox set are preserved rather than dropped on save.
    retryOnUnmodeled: [...new Set(storedRetryOn.filter(c => !OPT_IN_STATUSES.includes(c)))],
    retryOnNetworkErrors: retry?.retryOnNetworkErrors === true,
    retryPassthrough: retryPassthrough as Record<string, unknown>,
    rateLimitRows: (retry?.rateLimitHeaders ?? []).filter(Boolean).map(h => ({
      id: crypto.randomUUID(),
      name: h.name ?? '',
      format: normalizeFormat(h.format),
    })),
  };


  // Body stored as raw template string (supports unquoted {{expr}} for numbers/bools)
  if (typeof bodyObj === 'string') {
    return {
      method: (config?.method as string) ?? 'GET',
      url: (config?.url as string) ?? '',
      headerRows: objToRows(config?.headers),
      bodyMode: 'raw' as 'kvp' | 'raw',
      bodyKvp: [] as KvRow[],
      bodyRaw: bodyObj,
      ...retryFields,
    };
  }

  const bodyIsComplex =
    bodyObj !== undefined &&
    (typeof bodyObj !== 'object' ||
      Array.isArray(bodyObj) ||
      Object.values(bodyObj as object).some(v => typeof v === 'object' && v !== null));

  return {
    method: (config?.method as string) ?? 'GET',
    url: (config?.url as string) ?? '',
    headerRows: objToRows(config?.headers),
    bodyMode: (bodyIsComplex ? 'raw' : 'kvp') as 'kvp' | 'raw',
    bodyKvp: bodyIsComplex ? [] : objToRows(bodyObj),
    bodyRaw: bodyIsComplex ? JSON.stringify(bodyObj, null, 2) : '',
    ...retryFields,
  };
}

// Hoisted to module scope so React doesn't treat it as a new component type on
// every render of HttpStepConfig (which would unmount/remount and lose focus).
export function KvTable({
  rows,
  setRows,
  keyPlaceholder,
  valuePlaceholder,
}: {
  rows: KvRow[];
  setRows: (r: KvRow[]) => void;
  keyPlaceholder: string;
  valuePlaceholder: string;
}) {
  return (
    <>
      {rows.length > 0 && (
        <div className="grid grid-cols-[1fr_1fr_auto] gap-1 text-xs text-muted-foreground px-0.5 mb-0.5">
          <span>Key</span><span>Value</span><span />
        </div>
      )}
      <div className="space-y-1">
        {rows.map(r => (
          <div key={r.id} className="grid grid-cols-[1fr_1fr_auto] gap-1 items-start">
            <AutoResizeTextarea
              className="border border-input rounded p-1.5 text-xs font-mono bg-background"
              placeholder={keyPlaceholder}
              value={r.key}
              onChange={e => setRows(rows.map(x => x.id === r.id ? { ...x, key: e.target.value } : x))}
            />
            <AutoResizeTextarea
              className="border border-input rounded p-1.5 text-xs font-mono bg-background"
              placeholder={valuePlaceholder}
              value={r.value}
              onChange={e => setRows(rows.map(x => x.id === r.id ? { ...x, value: e.target.value } : x))}
            />
            <button
              onClick={() => setRows(rows.filter(x => x.id !== r.id))}
              className="text-muted-foreground hover:text-destructive px-1 text-sm leading-none"
              title="Remove"
            >×</button>
          </div>
        ))}
      </div>
      <button
        onClick={() => setRows([...rows, newRow()])}
        className="w-full border border-input text-xs text-muted-foreground rounded py-1 hover:bg-muted"
      >
        + Add row
      </button>
    </>
  );
}

interface Props {
  node: FlowNode;
  onUpdate: (updated: FlowNode) => void;
}

export function HttpStepConfig({ node, onUpdate }: Props) {
  const [method, setMethod] = useState(() => parseConfig(node.config).method);
  const [url, setUrl] = useState(() => parseConfig(node.config).url);
  const [headerRows, setHeaderRows] = useState<KvRow[]>(() => parseConfig(node.config).headerRows);
  const [bodyMode, setBodyMode] = useState<'kvp' | 'raw'>(() => parseConfig(node.config).bodyMode);
  const [bodyKvp, setBodyKvp] = useState<KvRow[]>(() => parseConfig(node.config).bodyKvp);
  const [bodyRaw, setBodyRaw] = useState(() => parseConfig(node.config).bodyRaw);
  const [bodyError, setBodyError] = useState<string | null>(null);
  const [label, setLabel] = useState(() => (node.config?.label as string) ?? '');
  const [alias, setAlias] = useState<string>((node.config?.alias as string) ?? '');
  const [retryEnabled, setRetryEnabled] = useState(() => parseConfig(node.config).retryEnabled);
  const [maxAttempts, setMaxAttempts] = useState(() => parseConfig(node.config).maxAttempts);
  const [maxDelaySeconds, setMaxDelaySeconds] = useState(() => parseConfig(node.config).maxDelaySeconds);
  const [timeoutSeconds, setTimeoutSeconds] = useState<number | null>(() => parseConfig(node.config).timeoutSeconds);
  const [allowStatusText, setAllowStatusText] = useState(() => parseConfig(node.config).allowStatusText);
  const [retryOn, setRetryOn] = useState<number[]>(() => parseConfig(node.config).retryOn);
  const [retryOnNetworkErrors, setRetryOnNetworkErrors] = useState(() => parseConfig(node.config).retryOnNetworkErrors);
  const [rateLimitRows, setRateLimitRows] = useState<RateLimitRow[]>(() => parseConfig(node.config).rateLimitRows);
  const [retryOnUnmodeled, setRetryOnUnmodeled] = useState<number[]>(() => parseConfig(node.config).retryOnUnmodeled);
  const [retryPassthrough, setRetryPassthrough] = useState<Record<string, unknown>>(
    () => parseConfig(node.config).retryPassthrough);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const p = parseConfig(node.config);
    setMethod(p.method);
    setUrl(p.url);
    setHeaderRows(p.headerRows);
    setBodyMode(p.bodyMode);
    setBodyKvp(p.bodyKvp);
    setBodyRaw(p.bodyRaw);
    setBodyError(null);
    setLabel((node.config?.label as string) ?? '');
    setAlias((node.config?.alias as string) ?? '');
    setRetryEnabled(p.retryEnabled);
    setMaxAttempts(p.maxAttempts);
    setMaxDelaySeconds(p.maxDelaySeconds);
    setTimeoutSeconds(p.timeoutSeconds);
    setAllowStatusText(p.allowStatusText);
    setRetryOn(p.retryOn);
    setRetryOnNetworkErrors(p.retryOnNetworkErrors);
    setRateLimitRows(p.rateLimitRows);
    setRetryOnUnmodeled(p.retryOnUnmodeled);
    setRetryPassthrough(p.retryPassthrough);
  }, [node.id]);

  function handleBodyModeChange(newMode: 'kvp' | 'raw') {
    if (newMode === bodyMode) return;
    if (newMode === 'raw') {
      const obj = rowsToObj(bodyKvp);
      if (Object.keys(obj).length > 0) {
        setBodyRaw(JSON.stringify(obj, null, 2));
      }
    } else {
      if (bodyRaw.trim()) {
        try {
          const parsed = JSON.parse(bodyRaw);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            setBodyKvp(objToRows(parsed));
          }
        } catch {
          // Invalid JSON — just switch mode without converting
        }
        setBodyError(null);
      }
    }
    setBodyMode(newMode);
  }

  function formatBody() {
    const raw = bodyRaw.trim();
    if (!raw) return;
    try {
      // Tokenise {{...}} so JSON.parse doesn't choke on template expressions.
      // Track whether each placeholder was originally inside quotes so we can
      // restore the surrounding quotes correctly after re-serialising.
      const tokens: Array<{ original: string; wasQuoted: boolean }> = [];

      let tokenized = raw.replace(/"(\{\{[^}]+\}\})"/g, (_, inner: string) => {
        const idx = tokens.length;
        tokens.push({ original: inner, wasQuoted: true });
        return `"__FF_${idx}__"`;
      });
      tokenized = tokenized.replace(/\{\{[^}]+\}\}/g, (match: string) => {
        const idx = tokens.length;
        tokens.push({ original: match, wasQuoted: false });
        return `"__FF_${idx}__"`;
      });

      const parsed = JSON.parse(tokenized);
      let formatted = JSON.stringify(parsed, null, 2);

      tokens.forEach(({ original, wasQuoted }, i) => {
        formatted = formatted.replace(`"__FF_${i}__"`, wasQuoted ? `"${original}"` : original);
      });

      setBodyRaw(formatted);
      setBodyError(null);
    } catch {
      setBodyError('Cannot format: invalid JSON structure');
    }
  }

  useEffect(() => {
    let body: string | Record<string, unknown> | undefined;
    if (bodyMode === 'kvp') {
      const obj = rowsToObj(bodyKvp);
      body = Object.keys(obj).length > 0 ? obj : undefined;
    } else if (bodyRaw.trim()) {
      try {
        JSON.parse(sanitizeTemplateForValidation(bodyRaw));
        setBodyError(null);
      } catch {
        setBodyError('Invalid JSON in body');
      }
      body = bodyRaw.trim();
    }
    const headers = rowsToObj(headerRows);
    const {
      headers: _h, body: _b, label: _l, alias: _a,
      retry: _r, timeoutSeconds: _t, allowStatus: _as, ...restConfig
    } = (node.config ?? {}) as Record<string, unknown>;

    // Emit nothing while the block is at its defaults, so untouched nodes keep an
    // unchanged config and the diff check below still short-circuits.
    const rlHeaders = rateLimitRows
      .filter(r => r.name.trim())
      .map(r => ({ name: r.name.trim(), format: r.format }));
    const allRetryOn = [...new Set([...retryOn, ...retryOnUnmodeled])].sort((a, b) => a - b);
    const retry = {
      ...retryPassthrough,
      ...(retryEnabled ? {} : { enabled: false }),
      ...(maxAttempts !== DEFAULT_MAX_ATTEMPTS ? { maxAttempts } : {}),
      ...(maxDelaySeconds !== DEFAULT_MAX_DELAY ? { maxDelaySeconds } : {}),
      ...(allRetryOn.length ? { retryOn: allRetryOn } : {}),
      ...(retryOnNetworkErrors ? { retryOnNetworkErrors: true } : {}),
      ...(rlHeaders.length ? { rateLimitHeaders: rlHeaders } : {}),
    };
    // Opening the panel must not rewrite a config nobody touched. `{"enabled": true}` and
    // `{"maxAttempts": 3}` are semantically default, so the rebuild above drops them — emitting
    // that would delete the key and mark the flow dirty. Keep the stored spelling when the
    // effective settings are unchanged.
    const storedRetry = _r as RetryCfg | undefined;
    const retryUnchanged =
      storedRetry !== undefined && JSON.stringify(effectiveRetry(storedRetry)) === JSON.stringify(effectiveRetry(retry));
    const retryOut = retryUnchanged ? storedRetry : retry;
    const allowStatus = parseStatusList(allowStatusText);

    const next = {
      ...restConfig,
      method,
      ...(label.trim() ? { label: label.trim() } : {}),
      ...(alias.trim() ? { alias: alias.trim() } : {}),
      ...(url.trim() ? { url } : {}),
      ...(Object.keys(headers).length > 0 ? { headers } : {}),
      ...(body !== undefined ? { body } : {}),
      ...(retryUnchanged || Object.keys(retry).length ? { retry: retryOut } : {}),
      ...(timeoutSeconds !== null ? { timeoutSeconds } : {}),
      ...(allowStatus.length ? { allowStatus } : {}),
    };
    if (JSON.stringify(next) === JSON.stringify(node.config ?? {})) return;
    const t = setTimeout(() => onUpdate({ ...node, config: next }), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method, url, headerRows, bodyMode, bodyKvp, bodyRaw, label, alias,
      retryEnabled, maxAttempts, maxDelaySeconds, timeoutSeconds, allowStatusText,
      retryOn, retryOnNetworkErrors, rateLimitRows, retryOnUnmodeled, retryPassthrough]);

  return (
    <div className="flex flex-col gap-3 flex-1 overflow-y-auto">
      {/* Label */}
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium">Label</label>
        <input
          type="text"
          className="border border-input rounded p-1.5 text-xs bg-background"
          placeholder="e.g. Get Project"
          value={label}
          onChange={e => setLabel(e.target.value)}
        />
      </div>

      {/* Alias */}
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium">Step alias</label>
        <input
          type="text"
          className="border border-input rounded p-1.5 text-xs font-mono bg-background"
          placeholder="e.g. get-project"
          value={alias}
          onChange={e => setAlias(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
        />
        {alias && (
          <p className="text-xs text-muted-foreground">
            Reference as{' '}
            <span className="font-mono text-primary">{`{{${alias}.field}}`}</span>
          </p>
        )}
      </div>

      {/* Method + URL */}
      <div className="flex gap-2">
        <select
          className="border border-input rounded p-1.5 text-xs bg-background font-medium"
          value={method}
          onChange={e => setMethod(e.target.value)}
        >
          {HTTP_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <AutoResizeTextarea
          className="flex-1 border border-input rounded p-1.5 text-xs font-mono bg-background"
          placeholder="https://api.example.com/{{body.id}}"
          value={url}
          onChange={e => setUrl(e.target.value)}
        />
      </div>

      {/* Headers */}
      <div>
        <p className="text-xs text-muted-foreground mb-1 font-medium">Headers</p>
        <KvTable
          rows={headerRows}
          setRows={setHeaderRows}
          keyPlaceholder="Authorization"
          valuePlaceholder="Bearer {{token-1.accessToken}}"
        />
      </div>

      {/* Body — shown for all methods except GET */}
      {method !== 'GET' && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-muted-foreground font-medium">Body</p>
            <div className="flex items-center gap-1.5">
              {bodyMode === 'raw' && (
                <button
                  onClick={formatBody}
                  title="Format JSON (Tab)"
                  className="text-xs text-muted-foreground hover:text-primary px-1.5 py-0.5 rounded border border-transparent hover:border-input transition-colors"
                >
                  Format
                </button>
              )}
              <div className="flex rounded border border-input overflow-hidden text-xs">
                {(['kvp', 'raw'] as const).map(m => (
                  <button
                    key={m}
                    onClick={() => handleBodyModeChange(m)}
                    className={`px-2 py-0.5 transition-colors ${
                      bodyMode === m
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {m === 'kvp' ? 'Fields' : 'Raw JSON'}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {bodyMode === 'kvp' ? (
            <KvTable
              rows={bodyKvp}
              setRows={setBodyKvp}
              keyPlaceholder="fieldName"
              valuePlaceholder="{{step.field}}"
            />
          ) : (
            <>
              <JsonTextarea
                className="w-full border border-input rounded p-1.5 text-xs font-mono bg-background min-h-[100px] resize-y"
                placeholder={'{\n  "key": "{{body.value}}"\n}'}
                value={bodyRaw}
                onChange={next => { setBodyRaw(next); setBodyError(null); }}
              />
              {bodyError && <p className="text-destructive text-xs mt-0.5">{bodyError}</p>}
            </>
          )}
        </div>
      )}

      {/* Status codes that do not fail the step */}
      <div className="flex flex-col gap-1">
        <label htmlFor={`${node.id}-allow-status`} className="text-xs text-muted-foreground font-medium">
          Continue on status
        </label>
        <input
          id={`${node.id}-allow-status`}
          type="text"
          className="border border-input rounded p-1.5 text-xs font-mono bg-background"
          placeholder="e.g. 404"
          value={allowStatusText}
          onChange={e => setAllowStatusText(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          These statuses do not fail the step. Branch on them with an Eval on{' '}
          <span className="font-mono">{`{{${alias || node.id}.status}}`}</span>. 429 always fails.
        </p>
      </div>

      {/* Retry & rate limits */}
      <details className="border border-input rounded">
        <summary className="text-xs font-medium px-2 py-1.5 cursor-pointer select-none">
          Retry &amp; rate limits
        </summary>
        <div className="flex flex-col gap-3 p-2 pt-1 border-t border-input">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={retryEnabled}
              onChange={e => setRetryEnabled(e.target.checked)}
            />
            <span>Retry on 429 (rate limit) &mdash; on by default</span>
          </label>

          <div className="grid grid-cols-3 gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground font-medium">Max attempts</label>
              <input
                type="number"
                min={1}
                max={10}
                className="border border-input rounded p-1.5 text-xs bg-background"
                value={maxAttempts}
                onChange={e => setMaxAttempts(
                  e.target.value === '' ? DEFAULT_MAX_ATTEMPTS : Number(e.target.value))}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground font-medium">Max wait (s)</label>
              <input
                type="number"
                min={0}
                max={600}
                className="border border-input rounded p-1.5 text-xs bg-background"
                value={maxDelaySeconds}
                onChange={e => setMaxDelaySeconds(
                  e.target.value === '' ? DEFAULT_MAX_DELAY : Number(e.target.value))}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground font-medium">Timeout (s)</label>
              <input
                type="number"
                min={1}
                placeholder="30"
                className="border border-input rounded p-1.5 text-xs bg-background"
                value={timeoutSeconds ?? ''}
                onChange={e => setTimeoutSeconds(e.target.value === '' ? null : Number(e.target.value))}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground font-medium">Also retry on</label>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {OPT_IN_STATUSES.map(code => (
                <label key={code} className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={retryOn.includes(code)}
                    onChange={e =>
                      setRetryOn(e.target.checked
                        ? [...retryOn, code]
                        : retryOn.filter(c => c !== code))
                    }
                  />
                  <span>{code}</span>
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-xs mt-1">
              <input
                type="checkbox"
                checked={retryOnNetworkErrors}
                onChange={e => setRetryOnNetworkErrors(e.target.checked)}
              />
              <span>Network and timeout errors</span>
            </label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Careful with POST/PATCH: a 5xx can mean the request already ran, so a retry may
              create a duplicate. A 429 never does.
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground font-medium">Rate limit headers</label>
            <p className="text-xs text-muted-foreground">
              <code>Retry-After</code> is always honoured. List extra headers here only if the
              API uses its own &mdash; the first one that matches wins.
            </p>
            {rateLimitRows.map((row, i) => (
              <div key={row.id} className="flex gap-1 items-center">
                <input
                  type="text"
                  className="border border-input rounded p-1.5 text-xs bg-background flex-1"
                  placeholder="X-RateLimit-Reset"
                  value={row.name}
                  onChange={e => {
                    const next = [...rateLimitRows];
                    next[i] = { ...row, name: e.target.value };
                    setRateLimitRows(next);
                  }}
                />
                <select
                  className="border border-input rounded p-1.5 text-xs bg-background"
                  value={row.format}
                  onChange={e => {
                    const next = [...rateLimitRows];
                    next[i] = { ...row, format: e.target.value };
                    setRateLimitRows(next);
                  }}
                >
                  {!isKnownFormat(row.format) && (
                    <option value={row.format}>{row.format} (invalid)</option>
                  )}
                  {HEADER_FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <button
                  type="button"
                  aria-label={`Remove ${row.name || 'header'}`}
                  className="text-muted-foreground hover:text-foreground px-1"
                  onClick={() => setRateLimitRows(rateLimitRows.filter(r => r.id !== row.id))}
                >
                  &times;
                </button>
              </div>
            ))}
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground self-start"
              onClick={() => setRateLimitRows([
                ...rateLimitRows,
                { id: crypto.randomUUID(), name: '', format: 'seconds' },
              ])}
            >
              + Add header
            </button>
          </div>
        </div>
      </details>

    </div>
  );
}
